import { FILTER_RELATIVE_DATE_RANGE, FilterTypeEnum as FilterType } from '@creditsights/api-ui-web';

export { FilterDto, FilterItemDto, FilterTypeEnum as FilterType } from '@creditsights/api-ui-web';

export interface FilterItem {
  tagId: string;
  displayName: string;
  children?: FilterItem[];
}

export interface Filter {
  type: FilterType;
  options: FilterItem[];
}

export enum RiskFilterType {
  acr_level_4 = 'acr_level_4',
  cqd = 'cqd',
  cqs = 'cqs',
  cqs_outlook = 'cqs_outlook',
  cqs_trend_1y = 'cqs_trend_1y',
  cqs_trend_3y = 'cqs_trend_3y',
  cqs_volatility = 'cqs_volatility',
  cre_1y = 'cre_1y',
  credit_risk_level = 'credit_risk_level',
  credit_risk_trend_6m = 'credit_risk_trend_6m',
  fas = 'fas',

  total_debt = 'total_debt',
  total_assets = 'total_assets',
}

export enum CoreScoreFilterType {
  assessment = 'assessment',
  outlook = 'outlook',
  sectorAssessment = 'sectorAssessment',
  sectorOutlook = 'sectorOutlook',
  eventAssessment = 'eventAssessment',
  eventOutlook = 'eventOutlook',
  cashAssessment = 'cashAssessment',
  cashOutlook = 'cashOutlook',
  leverageAssessment = 'leverageAssessment',
  leverageOutlook = 'leverageOutlook',
  liquidityAssessment = 'liquidityAssessment',
  liquidityOutlook = 'liquidityOutlook',
  profitabilityAssessment = 'profitabilityAssessment',
  profitabilityOutlook = 'profitabilityOutlook',
  capitalAssessment = 'capitalAssessment',
  capitalOutlook = 'capitalOutlook',
  assetQualityAssessment = 'assetQualityAssessment',
  assetQualityOutlook = 'assetQualityOutlook',
}
export const CREDIT_GRADE_SPECIAL_SITUATIONS_TAG = 'tag/special_situations';
export const DateFilterOption = 'date';
export const KeywordFilterOption = 'keyword';
export const CSViewOption = 'csView';
export const CoverageOption = 'coverage';
export const SearchTextOption = 'searchText';
export const WatchListOption = 'watchlistId';
export const CoverageOperationOption = 'coverageOperation';
export const IssuerOption = 'issuer';

export type FilterOption =
  | FilterType
  | typeof DateFilterOption
  | typeof KeywordFilterOption
  | typeof CSViewOption
  | typeof IssuerOption;
export type RiskFilterOption = RiskFilterType;
export type CoreScoreFilterOption = CoreScoreFilterType;
export type IssuersFilterOption = FilterOption | RiskFilterOption | CoreScoreFilterOption;

export const FILTER_TYPE_LABELS_SINGULAR: Record<FilterType, string> = {
  [FilterType.creditGrade]: 'Credit Grade',
  [FilterType.region]: 'Region',
  [FilterType.source]: 'Content Type',
  [FilterType.news_source]: 'Content Type',
  [FilterType.covenants_source]: 'Content Type',
  [FilterType.sector]: 'Sector/Subsector',
  [FilterType.topic]: 'Topic',
  [FilterType.report]: 'Report',
  [FilterType.assetClass]: 'Asset Class',
  [FilterType.country]: 'Country or Territory',
  [FilterType.currency]: 'Currency',
  [FilterType.analyst]: 'Analyst',
};

export const FILTER_TYPE_LABELS_PLURAL: Record<FilterType, string> = {
  [FilterType.creditGrade]: 'Credit Grades',
  [FilterType.region]: 'Regions',
  [FilterType.source]: 'Content Types',
  [FilterType.news_source]: 'Content Types',
  [FilterType.covenants_source]: 'Content Types',
  [FilterType.sector]: 'Sectors/Subsectors',
  [FilterType.topic]: 'Topics',
  [FilterType.report]: 'Reports',
  [FilterType.assetClass]: 'Asset Classes',
  [FilterType.country]: 'Country or Territory',
  [FilterType.currency]: 'Currencies',
  [FilterType.analyst]: 'Analysts',
};

export const RISK_FILTER_TYPE_LABELS: Record<RiskFilterType, string> = {
  [RiskFilterType.acr_level_4]: 'ACR Grouping',
  [RiskFilterType.cqd]: 'Credit Quality Differential (CQD)',
  [RiskFilterType.cqs]: 'Credit Quality Score (CQS)',
  [RiskFilterType.cqs_outlook]: 'CQS Outlook',
  [RiskFilterType.cqs_trend_1y]: 'CQS Trend 1Y',
  [RiskFilterType.cqs_trend_3y]: 'CQS Trend 3Y',
  [RiskFilterType.cqs_volatility]: 'CQS Volatility',
  [RiskFilterType.fas]: 'Fallen Angel Score (FAS)',
  [RiskFilterType.cre_1y]: 'CRE',
  [RiskFilterType.credit_risk_level]: 'Credit Risk Level (CRL)',
  [RiskFilterType.credit_risk_trend_6m]: 'CRE Trend 6M',
  [RiskFilterType.total_debt]: 'Total Debt (in Millions) USD',
  [RiskFilterType.total_assets]: 'Total Assets (in Millions) USD',
};

export const CORE_SCORE_FILTER_TYPE_LABELS: Record<CoreScoreFilterType, string> = {
  [CoreScoreFilterType.assessment]: 'Score',
  [CoreScoreFilterType.outlook]: 'Outlook',
  [CoreScoreFilterType.sectorAssessment]: 'Sector Risk Score',
  [CoreScoreFilterType.sectorOutlook]: 'Sector Risk Outlook',
  [CoreScoreFilterType.eventAssessment]: 'Event Risk Score',
  [CoreScoreFilterType.eventOutlook]: 'Event Risk Outlook',
  [CoreScoreFilterType.cashAssessment]: 'Cash Flow Score',
  [CoreScoreFilterType.cashOutlook]: 'Cash Flow Outlook',
  [CoreScoreFilterType.leverageAssessment]: 'Leverage Score',
  [CoreScoreFilterType.leverageOutlook]: 'Leverage Outlook',
  [CoreScoreFilterType.liquidityAssessment]: 'Liquidity Score',
  [CoreScoreFilterType.liquidityOutlook]: 'Liquidity Outlook',
  [CoreScoreFilterType.profitabilityAssessment]: 'Profitability Score',
  [CoreScoreFilterType.profitabilityOutlook]: 'Profitability Outlook',
  [CoreScoreFilterType.capitalAssessment]: 'Capital Score',
  [CoreScoreFilterType.capitalOutlook]: 'Capital Outlook',
  [CoreScoreFilterType.assetQualityAssessment]: 'Asset Quality Score',
  [CoreScoreFilterType.assetQualityOutlook]: 'Asset Quality Outlook',
};

export interface TreeModelParameters {
  options: FilterItem[];
  filter?: (item: FilterItem) => boolean;
}

export interface TagOptionParameters {
  option: FilterItem;
  filter?: (item: FilterItem) => boolean;
  currentDepth?: number;
}

export interface Filters {
  tags?: Record<string, string[]>;
  startDate?: Date;
  endDate?: Date;
  relativeRange?: FILTER_RELATIVE_DATE_RANGE;
  titleContains?: string[];
  keyword?: string;
  recWeightings?: string[];
  watchlistId?: string;
  ranges?: Record<string, FilterRange>;
  coverage?: string[];
  searchText?: string;
  coverageOperation?: 'or' | 'and';
}

export type FilterRange = {
  from?: number;
  to?: number;
  excluded?: boolean;
  isFractional?: boolean;
};

export type FilterGroups = {
  quickFilters: IssuersFilterOption[];
  riskFilters: IssuersFilterOption[];
  coreScoreFilters: IssuersFilterOption[];
};
